import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CosmicHero.module.css';

const NAKSHATRAS = [
  'Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu',
  'Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta',
  'Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha',
  'Uttara Ashadha','Shravana','Dhanishta','Shatabhisha','Purva Bhadrapada',
  'Uttara Bhadrapada','Revati'
];

const PLANETS = [
  { name: 'Chandra', cls: 'planetChandra', orbit: 80, speed: 0.8, angle: 0 },
  { name: 'Budh', cls: 'planetBudh', orbit: 115, speed: 1.2, angle: 45 },
  { name: 'Shukra', cls: 'planetShukra', orbit: 155, speed: 0.6, angle: 120 },
  { name: 'Mangal', cls: 'planetMangal', orbit: 195, speed: 0.45, angle: 200 },
  { name: 'Brihaspati', cls: 'planetBrihaspati', orbit: 235, speed: 0.3, angle: 300 },
  { name: 'Shani', cls: 'planetShani', orbit: 270, speed: 0.2, angle: 170 },
  { name: 'Rahu', cls: 'planetRahu', orbit: 305, speed: -0.15, angle: 90 },
  { name: 'Ketu', cls: 'planetKetu', orbit: 335, speed: -0.18, angle: 270 },
];

const ORBIT_CLASSES = [
  'orbit1','orbit2','orbit3','orbit4','orbit5','orbit6','orbit7','orbit8'
];

const CosmicHero = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const starfieldRef = useRef(null);
  const solarSystemRef = useRef(null);
  const sunRef = useRef(null);
  const nakshatraRingRef = useRef(null);
  const compassRef = useRef(null);
  const heroTextRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const nakshatraTextRef = useRef(null);
  const transitionTextRef = useRef(null);
  const vastuTextRef = useRef(null);
  const particleFieldRef = useRef(null);
  const orbitRefs = useRef([]);
  const finalCtaRef = useRef(null);

  // Mutable state for animation
  const planetState = useRef(PLANETS.map(p => ({ ...p })));
  const timeRef = useRef(0);
  const lastTimestampRef = useRef(0);
  const rafRef = useRef(null);
  const shootingStarInterval = useRef(null);

  // Create stars
  useEffect(() => {
    const sf = starfieldRef.current;
    if (!sf) return;
    for (let i = 0; i < 300; i++) {
      const star = document.createElement('div');
      star.className = styles.star;
      const size = Math.random() * 2.5 + 0.5;
      star.style.cssText = `
        width: ${size}px; height: ${size}px;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
        --dur: ${2 + Math.random() * 4}s;
        --min-o: ${0.05 + Math.random() * 0.2};
        --max-o: ${0.4 + Math.random() * 0.6};
        animation-delay: ${Math.random() * 5}s;
      `;
      sf.appendChild(star);
    }
    return () => { sf.innerHTML = ''; };
  }, []);

  // Create shooting stars
  useEffect(() => {
    const sf = starfieldRef.current;
    if (!sf) return;
    shootingStarInterval.current = setInterval(() => {
      if (Math.random() > 0.5) {
        const star = document.createElement('div');
        star.className = styles.shootingStar;
        const angle = -15 - Math.random() * 30;
        star.style.cssText = `
          top: ${Math.random() * 40}%;
          left: ${Math.random() * 60 + 20}%;
          transform: rotate(${angle}deg);
        `;
        sf.appendChild(star);
        star.animate([
          { opacity: 0, transform: `rotate(${angle}deg) translateX(-50px)` },
          { opacity: 0.8, offset: 0.3 },
          { opacity: 0, transform: `rotate(${angle}deg) translateX(200px)` },
        ], { duration: 800 + Math.random() * 600, easing: 'ease-out' });
        setTimeout(() => star.remove(), 1500);
      }
    }, 3000);
    return () => clearInterval(shootingStarInterval.current);
  }, []);

  // Create nakshatra marks/names
  useEffect(() => {
    const ring = nakshatraRingRef.current;
    if (!ring) return;

    // Determine ring radius based on element size
    const ringSize = ring.offsetWidth || 700;
    const half = ringSize / 2;

    NAKSHATRAS.forEach((name, i) => {
      const angle = (i / 27) * 360;
      const mark = document.createElement('div');
      mark.className = styles.nakshatraMark;
      mark.style.transform = `rotate(${angle}deg)`;
      mark.style.transformOrigin = `50% ${half}px`;
      ring.appendChild(mark);

      const label = document.createElement('div');
      label.className = styles.nakshatraName;
      label.textContent = name;
      const rad = ((angle - 90) * Math.PI) / 180;
      const r = half * 0.97;
      const x = half + r * Math.cos(rad);
      const y = half + r * Math.sin(rad);
      label.style.left = x + 'px';
      label.style.top = y + 'px';
      let textAngle = angle;
      if (textAngle > 90 && textAngle < 270) textAngle += 180;
      label.style.transform = `translate(-50%, -50%) rotate(${textAngle}deg)`;
      ring.appendChild(label);
    });
    return () => { ring.innerHTML = ''; };
  }, []);

  // Create planet elements
  useEffect(() => {
    const sys = solarSystemRef.current;
    if (!sys) return;
    const containers = [];

    planetState.current.forEach(p => {
      const container = document.createElement('div');
      container.className = styles.planetContainer;

      const planet = document.createElement('div');
      planet.className = `${styles.planet} ${styles[p.cls]}`;

      const label = document.createElement('div');
      label.className = styles.planetLabel;
      label.textContent = p.name;

      planet.appendChild(label);
      container.appendChild(planet);
      sys.appendChild(container);

      p.el = container;
      containers.push(container);
    });

    return () => containers.forEach(c => c.remove());
  }, []);

  // Create particle field
  useEffect(() => {
    const pf = particleFieldRef.current;
    if (!pf) return;
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.className = styles.energyParticle;
      particle.style.cssText = `left: ${Math.random() * 100}%; top: ${Math.random() * 100}%;`;
      pf.appendChild(particle);
    }
    return () => { pf.innerHTML = ''; };
  }, []);

  // Animation loop
  const getScrollProgress = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const scrollTop = window.scrollY;
    const maxScroll = container.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(1, scrollTop / maxScroll));
  }, []);

  useEffect(() => {
    const animate = (timestamp) => {
      const dt = lastTimestampRef.current ? (timestamp - lastTimestampRef.current) / 1000 : 0.016;
      lastTimestampRef.current = timestamp;
      timeRef.current += dt;

      const progress = getScrollProgress();
      const time = timeRef.current;

      // Update planets
      const scrollBoost = 1 + progress * 4;
      planetState.current.forEach(p => {
        if (!p.el) return;
        p.angle += p.speed * dt * 60 * scrollBoost;
        const rad = (p.angle * Math.PI) / 180;
        let orbitR = p.orbit;
        if (progress > 0.55) {
          const collapseT = Math.min(1, (progress - 0.55) / 0.2);
          orbitR = p.orbit * (1 - collapseT * 0.6);
        }
        let opacity = 1;
        if (progress > 0.65) {
          opacity = Math.max(0, 1 - (progress - 0.65) / 0.15);
        }
        const x = Math.cos(rad) * orbitR;
        const y = Math.sin(rad) * orbitR;
        p.el.style.transform = `translate(${x}px, ${y}px)`;
        p.el.style.opacity = opacity;
      });

      // Update nakshatras
      const ring = nakshatraRingRef.current;
      if (ring) {
        const marks = ring.querySelectorAll(`.${styles.nakshatraMark}`);
        const names = ring.querySelectorAll(`.${styles.nakshatraName}`);
        const showT = progress > 0.12 ? Math.min(1, (progress - 0.12) / 0.1) : 0;
        const hideT = progress > 0.5 ? Math.min(1, (progress - 0.5) / 0.15) : 0;
        marks.forEach((m, i) => {
          const delay = i / 27;
          m.style.opacity = Math.max(0, Math.min(1, (showT - delay * 0.5) * 2)) * (1 - hideT) * 0.6;
        });
        names.forEach((n, i) => {
          const delay = i / 27;
          n.style.opacity = Math.max(0, Math.min(1, (showT - delay * 0.3 - 0.2) * 3)) * (1 - hideT) * 0.55;
        });
        ring.style.transform = `rotate(${progress * 120}deg)`;
      }

      // Update compass
      const compass = compassRef.current;
      if (compass) {
        if (progress > 0.6) {
          const t = Math.min(1, (progress - 0.6) / 0.2);
          compass.style.opacity = t;
          compass.style.transform = `rotate(${(1 - t) * 45}deg) scale(${0.5 + t * 0.5})`;
        } else {
          compass.style.opacity = 0;
        }
      }

      // Update texts
      if (heroTextRef.current) {
        heroTextRef.current.style.opacity = progress < 0.12 ? 1 - progress / 0.12 : 0;
      }
      if (scrollIndicatorRef.current) {
        scrollIndicatorRef.current.style.opacity = progress < 0.05 ? 1 : Math.max(0, 1 - (progress - 0.05) / 0.05);
      }
      if (nakshatraTextRef.current) {
        if (progress > 0.14 && progress < 0.42) {
          const fadeIn = Math.min(1, (progress - 0.14) / 0.06);
          const fadeOut = progress > 0.35 ? Math.max(0, 1 - (progress - 0.35) / 0.07) : 1;
          nakshatraTextRef.current.style.opacity = fadeIn * fadeOut;
        } else {
          nakshatraTextRef.current.style.opacity = 0;
        }
      }
      if (transitionTextRef.current) {
        if (progress > 0.42 && progress < 0.68) {
          const fadeIn = Math.min(1, (progress - 0.42) / 0.06);
          const fadeOut = progress > 0.6 ? Math.max(0, 1 - (progress - 0.6) / 0.08) : 1;
          transitionTextRef.current.style.opacity = fadeIn * fadeOut;
        } else {
          transitionTextRef.current.style.opacity = 0;
        }
      }
      if (vastuTextRef.current) {
        if (progress > 0.72) {
          const fadeIn = Math.min(1, (progress - 0.72) / 0.08);
          vastuTextRef.current.style.opacity = fadeIn;
        } else {
          vastuTextRef.current.style.opacity = 0;
        }
      }
      // Final CTA visibility
      if (finalCtaRef.current) {
        if (progress > 0.78) {
          const fadeIn = Math.min(1, (progress - 0.78) / 0.08);
          finalCtaRef.current.style.opacity = fadeIn;
        } else {
          finalCtaRef.current.style.opacity = 0;
        }
      }

      // Sun transition
      const sun = sunRef.current;
      if (sun && progress > 0.6) {
        const t = Math.min(1, (progress - 0.6) / 0.2);
        const scale = 1 - t * 0.4;
        sun.style.transform = `scale(${scale})`;
        sun.style.boxShadow = `
          0 0 ${40 - t * 20}px ${10 - t * 5}px rgba(255,183,77,${0.5 - t * 0.3}),
          0 0 ${80 - t * 40}px ${30 - t * 15}px rgba(255,152,0,${0.3 - t * 0.2}),
          0 0 ${120 - t * 60}px ${50 - t * 25}px rgba(255,111,0,${0.15 - t * 0.1}),
          inset 0 0 20px rgba(255,255,255,${0.3 + t * 0.2})
        `;
        const r = Math.round(255 - t * 59);
        const g = Math.round(183 + t * 12);
        const b = Math.round(77 - t * 18);
        sun.style.background = `radial-gradient(circle at 35% 35%, #FFF8E1, rgb(${r},${g},${b}) 50%, #8B6914)`;
      }

      // Particles
      const pf = particleFieldRef.current;
      if (pf) {
        if (progress > 0.45 && progress < 0.75) {
          pf.style.opacity = 1;
          const t = (progress - 0.45) / 0.3;
          const particles = pf.children;
          const cx = window.innerWidth / 2;
          const cy = window.innerHeight / 2;
          for (let i = 0; i < particles.length; i++) {
            const p = particles[i];
            const angle = (i / 50) * Math.PI * 2 + time * 0.5;
            const startR = 400 + (i % 7) * 30;
            const endR = 100 + (i % 5) * 40;
            const r = startR - (startR - endR) * t;
            p.style.left = (cx + Math.cos(angle) * r) + 'px';
            p.style.top = (cy + Math.sin(angle) * r) + 'px';
            p.style.opacity = Math.sin(t * Math.PI) * 0.6;
            const sz = (2 + Math.sin(angle + time) * 1.5) + 'px';
            p.style.width = sz;
            p.style.height = sz;
            p.style.background = i % 3 === 0 ? '#C4973B' : '#E8D5A3';
          }
        } else {
          pf.style.opacity = 0;
        }
      }

      // System scale
      const sys = solarSystemRef.current;
      if (sys) {
        let scale = 1;
        const rotation = progress * 30;
        if (progress > 0.15 && progress < 0.4) {
          scale = 1 + (progress - 0.15) * 0.3;
        } else if (progress > 0.4) {
          const baseScale = 1 + 0.25 * 0.3;
          if (progress < 0.7) {
            scale = baseScale + Math.sin((progress - 0.4) * 10) * 0.02;
          } else {
            const t = Math.min(1, (progress - 0.7) / 0.15);
            scale = baseScale * (1 - t) + 0.85 * t;
          }
        }
        sys.style.transform = `scale(${scale}) rotate(${rotation}deg)`;
      }

      // Orbit ring fade
      orbitRefs.current.forEach(o => {
        if (!o) return;
        if (progress > 0.6) {
          const t = Math.min(1, (progress - 0.6) / 0.2);
          o.style.opacity = 1 - t;
        } else {
          o.style.opacity = 1;
        }
      });

      // Background shift
      const container = scrollContainerRef.current;
      if (container && progress > 0.7) {
        const t = Math.min(1, (progress - 0.7) / 0.2);
        const r = Math.round(10 + t * 10);
        const g = Math.round(14 + t * 8);
        const b = Math.round(26 - t * 6);
        container.style.background = `rgb(${r},${g},${b})`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [getScrollProgress]);

  return (
    <div className={styles.scrollContainer} ref={scrollContainerRef}>
      <div className={styles.stickyViewport}>
        {/* Starfield */}
        <div className={styles.starfield} ref={starfieldRef} />

        {/* Nebula */}
        <div className={`${styles.nebula} ${styles.nebula1}`} />
        <div className={`${styles.nebula} ${styles.nebula2}`} />
        <div className={`${styles.nebula} ${styles.nebula3}`} />

        {/* Solar System */}
        <div className={styles.solarSystem} ref={solarSystemRef}>
          {/* Sun */}
          <div className={styles.sun} ref={sunRef} />

          {/* Orbit rings */}
          {ORBIT_CLASSES.map((cls, i) => (
            <div
              key={cls}
              className={`${styles.orbit} ${styles[cls]}`}
              ref={el => { orbitRefs.current[i] = el; }}
            />
          ))}

          {/* Nakshatra outer ring */}
          <div className={styles.nakshatraRing} ref={nakshatraRingRef} />

          {/* Vastu Compass */}
          <div className={styles.vastuCompass} ref={compassRef}>
            <div className={`${styles.compassLine} ${styles.compassLineV}`} />
            <div className={`${styles.compassLine} ${styles.compassLineH}`} />
            <div className={`${styles.compassLine} ${styles.compassLineD1}`} />
            <div className={`${styles.compassLine} ${styles.compassLineD2}`} />
            <div className={styles.compassRing} />
            <div className={styles.compassRingInner} />
            <div className={styles.compassRingCore} />

            <div className={`${styles.directionLabel} ${styles.cardinal} ${styles.dirN}`}>N</div>
            <div className={`${styles.directionLabel} ${styles.cardinal} ${styles.dirS}`}>S</div>
            <div className={`${styles.directionLabel} ${styles.cardinal} ${styles.dirE}`}>E</div>
            <div className={`${styles.directionLabel} ${styles.cardinal} ${styles.dirW}`}>W</div>
            <div className={`${styles.directionLabel} ${styles.ordinal} ${styles.dirNE}`}>NE</div>
            <div className={`${styles.directionLabel} ${styles.ordinal} ${styles.dirNW}`}>NW</div>
            <div className={`${styles.directionLabel} ${styles.ordinal} ${styles.dirSE}`}>SE</div>
            <div className={`${styles.directionLabel} ${styles.ordinal} ${styles.dirSW}`}>SW</div>
          </div>
        </div>

        {/* Hero Text (Phase 1) */}
        <div className={styles.heroText} ref={heroTextRef}>
          <div className={styles.heroEyebrow}>VastuLens</div>
          <h1 className={styles.heroTitle}>The <em>Cosmos</em> Shapes<br />Your Space</h1>
          <p className={styles.heroSubtitle}>Scroll to explore the celestial architecture of Vastu</p>
          <div className={styles.ctaRow}>
            <button className={styles.ctaPrimary} onClick={() => navigate('/analysis')}>
              Analyze Your Floor Plan
            </button>
            <button className={styles.ctaSecondary} onClick={() => navigate('/compare')}>
              Compare Two Properties
            </button>
          </div>
        </div>

        {/* Phase Texts */}
        <div className={`${styles.phaseText} ${styles.phaseTextTop}`} ref={nakshatraTextRef}>
          <h2>27 <em>Nakshatras</em></h2>
          <p>The lunar mansions that govern spatial energy</p>
        </div>

        <div className={`${styles.phaseText} ${styles.phaseTextTop}`} ref={transitionTextRef}>
          <h2>From <em>Cosmos</em> to Compass</h2>
          <p>Where celestial alignment meets architectural wisdom</p>
        </div>

        <div className={`${styles.phaseText} ${styles.phaseTextTop}`} ref={vastuTextRef}>
          <h2>The <em>Vastu</em> Mandala</h2>
          <p>Eight directions &middot; Five elements &middot; One harmonious space</p>
          <div className={styles.finalCta} ref={finalCtaRef}>
            <button className={styles.ctaPrimary} onClick={() => navigate('/analysis')}>
              Begin Your Analysis
            </button>
          </div>
        </div>

        {/* Particle field */}
        <div className={styles.particleField} ref={particleFieldRef} />

        {/* Scroll indicator */}
        <div className={styles.scrollIndicator} ref={scrollIndicatorRef}>
          <span>Scroll</span>
          <div className={styles.scrollLine} />
        </div>
      </div>
    </div>
  );
};

export default CosmicHero;
